import { ComponentType, useMemo } from "react";
import { parseISO } from "date-fns";
import { TaskType } from "@common/types/task";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import {
    ReportTaskType,
    readReportComparisonOptions,
    readReportDisplayFilters,
} from "@components/reports/reportDisplayFilters";
import { SubScopeProvider } from "@state/subScopeContext";
import { serializePeriodDateViewConfig } from "./PeriodDateFields";
import { TemplateBalanceSheetDataTable } from "./dataPreview/TemplateBalanceSheetDataTable";
import { TemplatePreviewSwitcher } from "./dataPreview/TemplatePreviewSwitcher";
import { TemplateProfitLossDataTable } from "./dataPreview/TemplateProfitLossDataTable";
import { TemplateReportTableProps } from "./dataPreview/templateReportTable.types";
import { useTemplateTaskPreview } from "./dataPreview/useTemplateTaskPreview";
import { TemplateTaskPreviewProps } from "./templateTaskDrawer/templateTaskPreviewComponents";
import styles from "./CloseTemplateTaskReportPreview.module.scss";

const TABLE_BY_REPORT_TYPE: Record<
    ReportTaskType,
    ComponentType<TemplateReportTableProps>
> = {
    [TaskType.PROFIT_LOSS]: TemplateProfitLossDataTable,
    [TaskType.BALANCE_SHEET]: TemplateBalanceSheetDataTable,
};

const ROWS_LABEL_BY_REPORT_TYPE: Record<ReportTaskType, string> = {
    [TaskType.PROFIT_LOSS]: "Profit & loss",
    [TaskType.BALANCE_SHEET]: "Balance sheet",
};

interface Props extends Pick<TemplateTaskPreviewProps, "viewConfig"> {
    taskType: ReportTaskType;
}

const CloseTemplateTaskReportPreview = ({ taskType, viewConfig }: Props) => {
    const { previewTarget, resolvedView } = useTemplateTaskPreview({
        taskType,
        viewConfig:
            viewConfig ??
            serializePeriodDateViewConfig({
                taskType,
                viewConfig: null,
                months: null,
            }),
    });
    const { apiFilters, skipReason, isLoading } = resolvedView;
    const { entityId } = previewTarget;

    const tableProps = useMemo((): TemplateReportTableProps | null => {
        if (!apiFilters || entityId === null) {
            return null;
        }
        return {
            entityId,
            dateRange: {
                start: apiFilters.periodStart
                    ? parseISO(apiFilters.periodStart)
                    : undefined,
                end: apiFilters.periodEnd
                    ? parseISO(apiFilters.periodEnd)
                    : undefined,
            },
            cycle: readReportDisplayFilters(apiFilters).cycle,
            comparisonOptions: readReportComparisonOptions(apiFilters),
            canSwitchEntity: previewTarget.canSwitch,
        };
    }, [apiFilters, entityId, previewTarget.canSwitch]);

    const Table = TABLE_BY_REPORT_TYPE[taskType];

    const renderBody = () => {
        if (isLoading) {
            return <Loader />;
        }
        if (!tableProps) {
            return (
                <EmptyState
                    header="Nothing to preview"
                    body={
                        skipReason ??
                        `Update the settings to preview this ${ROWS_LABEL_BY_REPORT_TYPE[taskType]} task.`
                    }
                    emphasizeHeader={false}
                />
            );
        }
        return (
            <SubScopeProvider
                value={{ workspaceId: previewTarget.workspaceId }}
            >
                <Table key={tableProps.entityId} {...tableProps} />
            </SubScopeProvider>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <TemplatePreviewSwitcher target={previewTarget} />
            </div>
            <div className={styles.body}>{renderBody()}</div>
        </div>
    );
};

export const CloseTemplateTaskProfitLossPreview = ({
    viewConfig,
}: TemplateTaskPreviewProps) => (
    <CloseTemplateTaskReportPreview
        taskType={TaskType.PROFIT_LOSS}
        viewConfig={viewConfig}
    />
);

export const CloseTemplateTaskBalanceSheetPreview = ({
    viewConfig,
}: TemplateTaskPreviewProps) => (
    <CloseTemplateTaskReportPreview
        taskType={TaskType.BALANCE_SHEET}
        viewConfig={viewConfig}
    />
);
